"use client";

import React, { useState } from "react";
import PropTypes from "prop-types";
import TableThree from "@/components/Tables/TableThree";
import { IoMdPrint } from "react-icons/io";

/**
 * Tabla de resultados para "Reimpresión de Títulos Pagados".
 *
 * El grueso del trabajo (estilos, paginación, sort) lo hace TableThree
 * — que ya quedó actualizado para tema oscuro. Aquí solo definimos:
 *   - Las columnas
 *   - El botón "Imprimir" de cada fila
 *
 * Puntos a notar respecto al tema:
 *   - El botón "Imprimir" antes usaba la clase inexistente "btn-secundary"
 *     (typo). Lo corregimos a `btn-primary` de DaisyUI, que sí cambia con
 *     el tema. Como alternativa explícita y más controlable usamos
 *     tokens semánticos directamente — así no dependemos de DaisyUI para
 *     el botón pequeño dentro de filas.
 */
const Table = ({ isLoading, data = [], onPrintTitulo }) => {
  const [loadingRow, setLoadingRow] = useState(null);
  const [page, setPage] = useState(1);

  const handlePrint = async (contrapartida) => {
    setLoadingRow(contrapartida);
    try {
      await onPrintTitulo(contrapartida);
    } finally {
      setLoadingRow(null);
    }
  };

  const datatable = {
    head: [
      {
        title: "N° Título",
        key: "contrapartida",
        type: "text",
        sort: true,
      },
      {
        title: "Clave Predial",
        key: "clavepredial",
        type: "text",
        sort: true,
      },
      {
        title: "Nombre",
        key: "tipotitulo",
        type: "text",
        sort: true,
      },
      {
        title: "Fecha de pago",
        key: "fecha_pago",
        type: "text",
        sort: true,
      },
      {
        title: "Acción",
        type: "html",
        extend: (row) => (
          // Botón pequeño — usa color primario, hover suave, y se ve igual
          // de bien en claro y en oscuro. El icono es siempre blanco
          // porque va sobre el azul de marca en ambos temas.
          <button
            onClick={() => handlePrint(row.contrapartida)}
            className="inline-flex items-center justify-center rounded-lg px-3 py-2
                       bg-primary text-white border-none
                       hover:bg-primary/90 dark:bg-dark-primary dark:text-primary
                       dark:hover:bg-dark-primary/90
                       disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loadingRow === row.contrapartida}
            type="button"
            title="Reimprimir título"
          >
            {loadingRow === row.contrapartida ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : (
              <IoMdPrint size={20} />
            )}
          </button>
        ),
      },
    ],
    body: data,
  };

  const onChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <TableThree
      data={datatable}
      page={page}
      dataCount={datatable.body.length}
      numbered={true}
      isLoading={isLoading}
      onChange={onChange}
    />
  );
};

export default Table;

Table.propTypes = {
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onPrintTitulo: PropTypes.func.isRequired,
};
